import type { ServerToClientEvents } from "@iec-id/shared/contracts/_shared";
import { notify } from "@iec-id/ui";
import type { TFunction } from "i18next";
import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
// Только тип: значение (`io`) приезжает динамическим импортом ниже. Тип
// стирается при сборке и статического ребра в граф не добавляет — если написать
// здесь `import { io }`, сборщик честно вернёт socket.io в основной бандл, а
// ленивость останется на словах.
import type { Socket } from "socket.io-client";
import { NOTIFICATIONS_NS } from "@/components/notifications/i18n";
import { redirectToSignIn } from "@/lib/session";
import { api } from "@/lib/trpc";
import { i18n } from "./i18n.js";

/**
 * Socket.IO витрины: единственная точка подписки на личные события аккаунта.
 *
 * Правило «во все поверхности» (rules/lessons-learned §9): величина, изменённая
 * событием, обязана обновиться везде, где показана. Баланс Баллов виден в шапке,
 * на Главной и в разделе «Баллы» — поэтому инвалидируется весь namespace
 * `points`, а не конкретный запрос: точечная инвалидация одной поверхности —
 * ровно тот баг, из-за которого шапка «отстаёт» от карточки.
 *
 * Карта событий импортируется из `@iec-id/shared` и НЕ переписывается здесь.
 * Копия у витрины уже была и уже разошлась с сервером (`notification:new` без
 * `unread`, `notification:read` как `{id: string}` вместо
 * `{id: string | null; unread: number}`) — обработчики тела не читали, поэтому
 * расхождение ничего не ломало и потому жило. Одно объявление на обе стороны
 * закрывает этот класс целиком (грабли §27 «второй источник правды»).
 */
type AppSocket = Socket<ServerToClientEvents, Record<string, never>>;

/** Тот же origin, что у API: сессия живёт в cookie на `*.iec.exchange`. */
function socketUrl(): string {
	const explicit = import.meta.env.VITE_SOCKET_URL ?? import.meta.env.VITE_API_URL;
	if (explicit) return explicit;
	return typeof window === "undefined" ? "" : window.location.origin;
}

/**
 * Клиент Socket.IO грузится отдельным чанком, а не вместе с оболочкой.
 *
 * `socket.io-client` с транспортом — ~40 кБ в первом запросе ради канала,
 * который нужен только для догоняющих обновлений: баланс, уведомления и отзыв
 * сессии одинаково приезжают обычным запросом при открытии раздела. Платить за
 * него до первой отрисовки незачем — подписка поднимается следом.
 */
async function createSocket(): Promise<AppSocket> {
	const { io } = await import("socket.io-client");
	return io(socketUrl(), {
		path: "/socket.io",
		withCredentials: true,
		transports: ["websocket", "polling"],
		// Backoff 1s → 30s: при rolling update разом переподключаются все вкладки,
		// а фиксированный интервал превращает их в шторм на первом же живом узле.
		reconnection: true,
		reconnectionDelay: 1000,
		reconnectionDelayMax: 30_000,
		randomizationFactor: 0.5,
	});
}

type Handlers = { utils: ReturnType<typeof api.useUtils>; t: TFunction };

/**
 * Подписки на события сервера. Вынесены из хука наружу, потому что соединение
 * теперь поднимается асинхронно: держать сотню строк внутри `.then()` — верный
 * способ однажды не заметить, что часть обработчиков навесилась дважды.
 *
 * Свежие `utils`/`t` берутся вызовом `getHandlers()`, а не захватом: соединение
 * живёт весь визит, а замыкание на первый рендер устарело бы сразу.
 */
function subscribe(socket: AppSocket, getHandlers: () => Handlers): void {
	socket.on("notification:new", (payload) => {
		const { utils: u, t: translate } = getHandlers();
		void u.notifications.invalidate();
		// Наличие ключа проверяется явно, а не через `defaultValue`: обработчик
		// промахов возвращает саму строку ключа, и до `defaultValue` дело не
		// доходит — тост показывал бы сырой `kind.account.…`. Та же ступенька,
		// что в строке ленты (`notification-row.tsx`).
		const key = `kind.${payload.kind}`;
		const known = i18n.exists(key, { ns: NOTIFICATIONS_NS });
		notify.info(translate(known ? key : "kind.unknown"));
	});

	socket.on("notification:read", () => {
		void getHandlers().utils.notifications.invalidate();
	});

	socket.on("points:balance-changed", () => {
		// Шапка, Главная и раздел «Баллы» читают разные процедуры одного
		// namespace — инвалидируется namespace целиком.
		void getHandlers().utils.points.invalidate();
		void getHandlers().utils.referrals.invalidate();
	});

	socket.on("entitlement:changed", () => {
		const { utils: u } = getHandlers();
		void u.entitlements.invalidate();
		void u.links.invalidate();
		void u.billing.invalidate();
	});

	socket.on("session:revoked", (payload) => {
		const { utils: u, t: translate } = getHandlers();
		void u.auth.invalidate();

		// Соседняя вкладка (`current: false`): человек сам завершил СВОЮ сессию
		// на другом устройстве. Выкидывать его отсюда нельзя — кнопку он нажимал
		// именно здесь; достаточно перечитать список в «Безопасности».
		if (!payload.current) return;

		notify.error(translate("common:realtime.sessionRevoked"), {
			description: translate("common:realtime.sessionRevokedDescription"),
		});
		// Погашена ИМЕННО эта сессия. Витрина за ней не живёт: всё, что здесь
		// показано, требует входа. Уводим на форму входа с возвратом сюда, а не
		// ждём, пока это обнаружится очередным 401 в виде «Не удалось загрузить».
		redirectToSignIn();
	});

	socket.on("legal:new-version", () => {
		const { utils: u, t: translate } = getHandlers();
		void u.legal.invalidate();
		void u.consents.invalidate();
		notify.info(translate("common:realtime.legalNewVersion"), {
			description: translate("common:realtime.legalNewVersionDescription"),
		});
	});

	// Разрыв дольше окна восстановления соединения означает пропущенные
	// события: перечитываем всё, иначе витрина молча показывает старое.
	socket.io.on("reconnect", () => {
		void getHandlers().utils.invalidate();
	});
}

/**
 * Монтируется один раз в оболочке витрины. Возвращать сокет наружу незачем:
 * клиент не шлёт серверу ничего (`ClientToServerEvents` пуст).
 */
export function useRealtime(): void {
	const utils = api.useUtils();
	// Namespace импортируется по константе, а не строкой: модуль `notifications/i18n`
	// регистрирует бандл на импорте, и без него тост про новое уведомление
	// показал бы ключ вместо текста тому, кто ни разу не открывал ленту.
	const { t } = useTranslation(NOTIFICATIONS_NS);
	// Хендлеры читают свежие utils/t через ref: пересоздавать соединение на
	// каждый рендер нельзя — переподключение теряет буфер пропущенных событий.
	const handlersRef = useRef<Handlers>({ utils, t });
	handlersRef.current = { utils, t };

	useEffect(() => {
		// Чанк с клиентом приезжает асинхронно, и размонтирование может его
		// опередить: без флага мы подняли бы соединение уже мёртвой оболочке, а
		// вернуть его наружу было бы уже некуда. В StrictMode этот порядок —
		// не редкий случай, а каждое монтирование.
		let disposed = false;
		let live: AppSocket | undefined;

		void createSocket().then((socket) => {
			if (disposed) {
				socket.disconnect();
				return;
			}
			live = socket;
			subscribe(socket, () => handlersRef.current);
		});

		return () => {
			disposed = true;
			if (!live) return;
			live.removeAllListeners();
			live.io.removeAllListeners();
			live.disconnect();
		};
	}, []);
}
